import { EmployerFeatureService } from '../employer-feature/employer-feature.service';
import MeService from '../me/me.service';
import TrackingService from '../tracking/tracking.service';
import { ACTIVE_SUBSCRIPTION_STATUSES } from './consts/active-subscription-statuses.const';
import { SubscriptionPersistenceV2 } from './subscription-v2.persistence';
import { useSubscriptionV2Store } from './subscription-v2.store';
import { SubscriptionPersistence } from './subscription.persistence';
import { useSubscriptionStore } from './subscription.store';
import type { UpgradeProposalTraits } from './types/upgrade-proposal-traits.type';
import type { ATSEmployerSubscription, Subscription } from '@factoryfixinc/ats-interfaces';
import {
  ATSProvider,
  ATSSubscriptionStatus,
  SubscriptionStatus,
} from '@factoryfixinc/ats-interfaces';
import chmln from '@chamaeleonidae/chmln';
import dayjs from 'dayjs';

// TODO: export EntitlementFeatureId from ats-interfaces
// https://linear.app/factoryfix/issue/ONE-4477/create-vendor-agnostic-entitlement-feature-enum-and-mapping#comment-9a05fcef
enum EntitlementFeatureId {
  GOLD = 'gold',
  GOLD_TRIAL = 'gold_trial',
}

export default class SubscriptionService {
  private static staticInstance: SubscriptionService;
  private store = useSubscriptionStore();
  private storeV2 = useSubscriptionV2Store();
  private persistence = new SubscriptionPersistence();
  private persistenceV2 = new SubscriptionPersistenceV2();
  private meService = new MeService();
  private trackingService = TrackingService.instance;
  private employerFeatureService = new EmployerFeatureService();

  /**
   * Returns a singleton instance of SubscriptionService.
   * This prevents multiple service instantiations which would create duplicate instances
   * of stores, persistence layers, and other services (MeService, TrackingService, etc.).
   * Use this method when you need to access SubscriptionService from other services
   * to ensure consistent state and avoid circular dependency issues.
   */
  static get instance(): SubscriptionService {
    if (!SubscriptionService.staticInstance) {
      SubscriptionService.staticInstance = new SubscriptionService();
    }
    return SubscriptionService.staticInstance;
  }

  public getGoldTrialProperties(): {
    gold_trial_started: boolean;
    trial_end_date: string | null;
    gold_trial_expired: boolean;
    subscription_upgraded: boolean;
    days_until_trial_end: number | null;
  } {
    const goldTrialEntitlement = this.storeV2.subscription?.entitlements?.find(
      (entitlement) => entitlement.featureId === EntitlementFeatureId.GOLD_TRIAL,
    );

    const goldEntitlement = this.storeV2.subscription?.entitlements?.find(
      (entitlement) => entitlement.featureId === EntitlementFeatureId.GOLD,
    );

    // Default values if no gold trial entitlement exists
    if (!goldTrialEntitlement) {
      return {
        gold_trial_started: false,
        trial_end_date: null,
        gold_trial_expired: false,
        subscription_upgraded: !!goldEntitlement, // Has gold entitlement means upgraded
        days_until_trial_end: null,
      };
    }

    // Gold trial exists
    // expiresAt is in milliseconds, use dayjs for proper date comparison
    const now = dayjs();
    const trialEndDateTime = goldTrialEntitlement.expiresAt
      ? dayjs(goldTrialEntitlement.expiresAt)
      : null;

    const trialExpired = trialEndDateTime ? trialEndDateTime.isBefore(now) : false;

    // Format trial end date if it exists
    const trialEndDate = trialEndDateTime ? trialEndDateTime.format('YYYY-MM-DD') : null;

    // Calculate days until trial end (using ceiling to count partial days as full days)
    const daysUntilTrialEnd = trialEndDateTime
      ? Math.max(0, Math.ceil(trialEndDateTime.diff(now, 'day', true)))
      : null;

    // Check if subscription was upgraded - has gold entitlement means they upgraded
    const subscriptionUpgraded = !!goldEntitlement;

    return {
      gold_trial_started: true,
      trial_end_date: trialEndDate,
      gold_trial_expired: trialExpired,
      subscription_upgraded: subscriptionUpgraded,
      days_until_trial_end: daysUntilTrialEnd,
    };
  }

  public get useV2(): boolean {
    return this.employerFeatureService.hasSubscriptionV2Enabled;
  }

  public get billingPortalUrl(): string {
    return this.useV2 ? this.storeV2.portalSessionUrl ?? '' : '';
  }

  public get subscription(): ATSEmployerSubscription | Subscription | undefined {
    return this.useV2 ? this.storeV2.subscription : this.store.subscription;
  }

  public get isMaxioSubscription(): boolean {
    return this.useV2 ? false : this.store.subscription?.provider === ATSProvider.MAXIO;
  }

  public get isSubscriptionFreePlan(): boolean {
    return this.useV2
      ? this.storeV2.subscription?.providerSubscriptionStatus === 'in_trial'
      : this.store.subscription?.status === ATSSubscriptionStatus.TRIAL;
  }

  public get isGatedSubscription(): boolean {
    return this.useV2 ? true : this.store.talentSearchGating?.isGated ?? false;
  }

  public get talentSearchCredits(): number {
    return this.useV2
      ? this.storeV2.subscription?.components?.TS_UNLOCK?.limit ?? 0
      : this.store.subscription?.talentSearchCredits ?? 0;
  }

  public get usedTalentSearchCredits(): number {
    return this.useV2
      ? this.storeV2.subscription?.components?.TS_UNLOCK?.used ?? 0
      : this.store.talentSearchGating?.usedCredits ?? 0;
  }

  public get jobSlots(): number {
    return this.useV2
      ? this.storeV2.subscription?.components.COPILOT_SLOT.limit ?? 0
      : this.store.subscription?.jobSlotQuantity ?? 0;
  }

  public get usedJobSlots(): number {
    return this.useV2
      ? this.storeV2.subscription?.components.COPILOT_SLOT.used ?? 0
      : this.store.jobsCount;
  }

  /**
   * True once fetchSubscription has completed successfully (for either the v1
   * or v2 path). Until then, `isEmployerSubscribed === false` only means
   * "unknown" — gates must not show the expired screen on it.
   */
  public get isSubscriptionLoaded(): boolean {
    return this.store.isLoaded;
  }

  public get isEmployerSubscribed(): boolean {
    let isSubscribed = false;

    if (this.useV2) {
      if (this.storeV2.subscription?.status === SubscriptionStatus.ACTIVE) {
        isSubscribed = true;
      }
    } else {
      if (this.store.subscription?.status) {
        isSubscribed = ACTIVE_SUBSCRIPTION_STATUSES.includes(this.store.subscription.status);
      }
    }

    return isSubscribed;
  }

  public updateCopilotsUsedLocalValue(addValue: number): void {
    if (this.useV2) {
      if (this.storeV2.subscription) {
        this.storeV2.subscription.components.COPILOT_SLOT.used += addValue;
      }
    } else {
      this.store.jobsCount += addValue;
    }
  }

  public shouldDisplayTrialModal(params: { unlockCount: number }): boolean {
    const { unlockCount } = params;
    const isTrial = this.isSubscriptionFreePlan;
    const tsCredits = this.talentSearchCredits;
    const usedTsCredits = this.usedTalentSearchCredits;
    const totalUsedTsCredits = usedTsCredits + unlockCount;

    return isTrial && totalUsedTsCredits > tsCredits;
  }

  public async fetchSubscription(): Promise<void> {
    const employerId = this.meService.employer?.id;
    if (!employerId) return;

    if (this.useV2) {
      this.storeV2.subscription = await this.persistenceV2.fetchSubscription(employerId);
    } else {
      this.store.subscription = await this.persistence.fetchSubscription(employerId);

      const isEmployerUser = this.meService.isEmployerUser;

      if (this.isEmployerSubscribed && isEmployerUser && employerId) {
        const [talentSearchGating, jobsCount] = await Promise.all([
          this.persistence.fetchTalentSearchGating(employerId),
          this.persistence.fetchJobsCount(employerId),
        ]);

        this.store.talentSearchGating = talentSearchGating;
        this.store.jobsCount = jobsCount;
      }
    }

    this.store.isLoaded = true;

    this.trackingService.identifyCompany();
  }

  public async fetchUpgradeProposal(): Promise<void> {
    const employerId = this.meService.employer?.id;
    if (!employerId) return;

    this.storeV2.upgradeProposal = await this.persistenceV2.fetchUpgradeProposal(employerId);

    if (this.storeV2.upgradeProposalTraits) {
      this.trackingService.identifySubscriptionPricing(this.storeV2.upgradeProposalTraits);
      this.identifyChameleonPricing(this.storeV2.upgradeProposalTraits);
    }
  }

  public async fetchPortalSessionUrl(): Promise<void> {
    const employerId = this.meService.employer?.id;
    if (!employerId) return;

    this.storeV2.portalSessionUrl = await this.persistenceV2.fetchPortalSessionUrl(employerId);
  }

  public clearPortalSessionUrl(): void {
    this.storeV2.portalSessionUrl = '';
  }

  private identifyChameleonPricing(traits: UpgradeProposalTraits): void {
    if (import.meta.env.DEV) return;

    const userId = this.meService.userProfile?.id;
    if (!userId) return;

    chmln.identify(userId, { ...traits });
  }
}
